import { logger } from '@automata/utils';
import { WorkflowConfig } from '@automata/wfb-api/src/apis/wfbApiSchemas';

import { WorkcellInstrumentsById } from '../../features/workflow/types';

/**
 * Splits a driver name with version suffix into separate name and version components.
 *
 * @param driverNameWithVersion - The driver name with version suffix (e.g., "myDriverName_v1")
 * @returns An object containing the driver name and version
 *
 * @example
 * ```typescript
 * splitDriverNameAndVersion("myDriverName_v1")
 * // Returns: { name: "myDriverName", version: "v1" }
 *
 * splitDriverNameAndVersion("anotherDriver_v2")
 * // Returns: { name: "anotherDriver", version: "v2" }
 *
 * splitDriverNameAndVersion("driverWithoutVersion")
 * // Returns: { name: "driverWithoutVersion", version: "v1" }
 * ```
 */
export const splitDriverNameAndVersion = (
  driverNameWithVersion: string
): { driverName: string; driverVersion: string } => {
  const versionRegex = /_v(\d+)$/;
  const match = driverNameWithVersion.match(versionRegex);

  if (!match) {
    logger.warn(
      `Driver name "${driverNameWithVersion}" does not follow the expected format "<name>_v<version_number>". Defaulting version to "v1".`
    );

    return { driverName: driverNameWithVersion, driverVersion: 'v1' };
  }

  const [_input, versionNumber] = match;

  return {
    driverName: driverNameWithVersion.replace(versionRegex, ''),
    driverVersion: `v${versionNumber}`,
  };
};

export const getWorkflowInstrumentsForSdk = (
  workflowConfig: WorkflowConfig
): WorkcellInstrumentsById =>
  workflowConfig.workcell.instruments.reduce<WorkcellInstrumentsById>(
    (acc, instrument) => {
      const { driverName, driverVersion } = splitDriverNameAndVersion(
        instrument.driver
      );

      const workcellInstrument: WorkcellInstrumentsById[string] = {
        bench: instrument.bench,
        capacity: instrument.capacity,
        config: instrument.config,
        mappings: instrument.mappings?.filter(
          (mapping): mapping is string => typeof mapping === 'string'
        ),
        is_slotless_container: instrument.is_slotless_container,
        driverName,
        driverVersion,
        simulate:
          typeof instrument.simulate === 'boolean'
            ? instrument.simulate
            : undefined,
      };

      if (instrument.driver === 'automata_sftp_v1') {
        workcellInstrument.dataConnectorConfig = {
          inputActions: ['write_file'],
        };
      }

      // note: this will need to be enabled in canvas `packages/workflow-builder/src/constants.ts`
      if (instrument.driver === 'automata_s3_v1') {
        workcellInstrument.dataConnectorConfig = {
          inputActions: ['upload_file'],
        };
      }

      return {
        ...acc,
        [instrument.id]: workcellInstrument,
      };
    },
    {}
  );
